import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import MarketplaceShell from './MarketplaceShell';
import { MktCard, MktButton, StatusBadge, studyHref } from './marketplaceUi';
import { UseCaseTile } from './useCaseMeta';
import { listStudies, dollars, type StudyHistoryItem } from './marketplaceApi';

const USE_CASE_LABEL: Record<string, string> = {
  survey: 'Qual / quant survey',
  concept: 'Concept test',
  packaging: 'Packaging test',
  chat: 'Chat',
};

const fmtDate = (iso?: string) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return ''; }
};

const MarketplaceHistory: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<StudyHistoryItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listStudies().then(setItems).catch((e) => setError(e.message));
  }, []);

  if (error) return <MarketplaceShell maxWidth="max-w-4xl"><div className="text-red-600">{error}</div></MarketplaceShell>;

  return (
    <MarketplaceShell maxWidth="max-w-4xl">
      <div className="flex items-center justify-between">
        <h1 className="text-[22px] font-medium text-[#111]">Your studies</h1>
        <MktButton onClick={() => navigate('/marketplace/run')}>New study →</MktButton>
      </div>
      <p className="mt-1.5 text-[13px] text-[#6b6b6b]">Past runs and chat sessions.</p>

      {!items ? (
        <div className="mt-8 text-[13px] text-[#6b6b6b]">Loading…</div>
      ) : items.length === 0 ? (
        <MktCard className="mt-6 p-8 text-center">
          <p className="text-[13px] text-[#6b6b6b]">You haven’t run any studies yet.</p>
          <div className="mt-4"><MktButton onClick={() => navigate('/marketplace/run')}>Run your first study →</MktButton></div>
        </MktCard>
      ) : (
        <div className="mt-6 space-y-3">
          {items.map((s) => {
            const isChat = s.use_case === 'chat';
            const dest = studyHref(s, s.id);
            return (
              <MktCard key={s.id} className="flex items-center justify-between p-4"
                    onClick={() => navigate(dest.path, dest.state ? { state: dest.state } : undefined)}>
                <div className="flex items-center gap-3">
                  <UseCaseTile useCaseKey={s.use_case} size={36} />
                  <div>
                    <div className="text-[13px] font-medium text-[#111]">{USE_CASE_LABEL[s.use_case] || s.use_case}</div>
                    <div className="text-[11px] text-[#999]">
                      {fmtDate(s.created_at)} · {s.twin_count} Twins · {s.is_free ? 'Included' : dollars(s.amount_cents || 0)}
                      {isChat && s.queries_allowed != null && (
                        <> · <span className="font-medium text-[#374151]">{s.queries_used}/{s.queries_allowed} queries used</span>
                          {(s.queries_remaining || 0) > 0 && <span className="text-primary"> · {s.queries_remaining} left</span>}
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge study={s} />
                  <ArrowRight className="h-4 w-4 text-[#999]" />
                </div>
              </MktCard>
            );
          })}
        </div>
      )}
    </MarketplaceShell>
  );
};

export default MarketplaceHistory;
